import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import {ChartOptions, TooltipItem} from 'chart.js';

import { MetricData } from 'features/admin/model/IMetrics';

type ChartPoint = {
    x: string;
    y: number;
    meta: {
        bucketStart: string;
        bucketEnd: string;
        aggregation: 'day' | 'week' | 'month';
    };
};

export const getChartOptions = (
    metricsData: MetricData[],
    aggregation: 'day' | 'week' | 'month'): ChartOptions<'line'> => {

    const calculateBounds = () => {
        if (metricsData.length === 0) {
            return { min: undefined, max: undefined };
        }

        const dates = metricsData
            .map(item => new Date(item.date_metrics.split('T')[0]).getTime())
            .sort((a, b) => a - b);

        if (dates.length === 1) {
            const oneDay = 24 * 60 * 60 * 1000;
            return {
                min: dates[0] - oneDay,
                max: dates[0] + oneDay,
            };
        }

        return {
            min: dates[0],
            max: dates[dates.length - 1],
        };
    };

    const bounds = calculateBounds();

    const getXAxisUnit = () => {
        switch(aggregation) {
            case 'week': return 'week';
            case 'month': return 'month';
            default: return 'day';
        }
    };

    const getDisplayFormat = () => {
        switch(aggregation) {
            case 'day': return { day: 'dd-MM-yy' };
            case 'week': return { week: 'dd-MM-yy' };
            case 'month': return { month: 'MMM yyyy' };
            default: return { day: 'dd-MM-yy' };
        }
    };

    return {
        responsive: true,
        maintainAspectRatio: false,

        interaction: {
            mode: 'index' as const,
            intersect: false,
        },

        elements: {
            point: { radius: 0, hoverRadius: 6 },
        },

        scales: {
            x: {
                type: 'time' as const,
                offset: metricsData.length > 1,
                time: {
                    unit: getXAxisUnit(),
                    tooltipFormat: 'dd-MM-yyyy',
                    displayFormats: getDisplayFormat(),
                },
                adapters: { date: { locale: ru } },

                ticks: {
                    source: 'data',
                    autoSkip: true,
                    maxTicksLimit: 10,
                    callback: (value: number | string) => {
                        const date = new Date(value);
                        return `${String(date.getDate()).padStart(2, '0')}-${String(
                            date.getMonth() + 1,
                        ).padStart(2, '0')}-${date.getFullYear().toString().slice(-2)}`;
                    },
                },
                min: bounds.min,
                max: bounds.max,
            },

            y: {
                beginAtZero: true,
                grace: '5%',
            },
        },

        plugins: {
            legend: { display: true },

            tooltip: {
                callbacks: {
                    title: (items: TooltipItem<'line'>[]) => {
                        if (!items.length) return '';

                        const raw = items[0].raw as ChartPoint;

                        if (raw?.meta?.aggregation !== 'day') {
                            const start = format(parseISO(raw.meta.bucketStart), 'dd-MM-yyyy');
                            const end = format(parseISO(raw.meta.bucketEnd), 'dd-MM-yyyy');
                            return `${start} - ${end}`;
                        }

                        return format(parseISO(raw.x), 'dd-MM-yyyy');
                    },

                    label: (ctx: TooltipItem<'line'>) => {
                        return `${ctx.dataset.label}: ${Math.round(ctx.parsed.y ?? 0).toLocaleString('ru-RU')}`;
                    },
                },
            },
        },
    };
};